/**
 * Atom components: LiveStatusPill, CountdownWidget, SectionHeader, badges, etc.
 */
import React from "react";
import { motion } from "framer-motion";
import { useCountdown } from "../hooks/useCountdown";

// ---------------------------------------------------------------------------
export function LiveStatusPill({ status = "demo", label }) {
    // status: live | replay | reconnecting | offline | upcoming
    const map = {
        live: {
            dot: "bg-f1-red",
            text: "text-f1-red",
            ring: "ring-f1-red/40",
            pulse: true,
            defaultLabel: "LIVE",
        },
        demo: {
            dot: "bg-amber-400",
            text: "text-amber-400",
            ring: "ring-amber-400/30",
            pulse: false,
            defaultLabel: "REPLAY MODE",
        },
        replay: {
            dot: "bg-f1-red",
            text: "text-f1-red",
            ring: "ring-f1-red/30",
            pulse: false,
            defaultLabel: "REPLAY MODE",
        },
        reconnecting: {
            dot: "bg-orange-400",
            text: "text-orange-400",
            ring: "ring-orange-400/30",
            pulse: true,
            defaultLabel: "RECONNECTING",
        },
        offline: {
            dot: "bg-zinc-500",
            text: "text-zinc-500",
            ring: "ring-zinc-500/30",
            pulse: false,
            defaultLabel: "OFFLINE",
        },
        upcoming: {
            dot: "bg-emerald-400",
            text: "text-emerald-400",
            ring: "ring-emerald-400/30",
            pulse: false,
            defaultLabel: "UPCOMING",
        },
    };
    const s = map[status] || map.demo;
    return (
        <span
            data-testid={`live-status-pill-${status}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass ring-1 ${s.ring}`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${s.dot} ${
                    s.pulse ? "pulse-red" : ""
                }`}
                style={s.pulse ? undefined : {}}
            />
            <span
                className={`font-mono text-[10px] tracking-[0.22em] font-semibold ${s.text}`}
            >
                {label || s.defaultLabel}
            </span>
        </span>
    );
}

// ---------------------------------------------------------------------------
export function CountdownWidget({ target, label }) {
    const { d, h, m, s, done } = useCountdown(target);
    const Cell = ({ value, unit }) => (
        <div className="flex flex-col items-center">
            <div className="font-display text-3xl sm:text-4xl tabular-nums tracking-tight leading-none">
                {String(value).padStart(2, "0")}
            </div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 mt-1.5">
                {unit}
            </div>
        </div>
    );
    return (
        <div
            data-testid="countdown-widget"
            className="inline-flex items-center gap-4 sm:gap-6"
        >
            {label && (
                <div className="hidden sm:flex flex-col items-start pr-4 border-r border-white/10">
                    <div className="label-eyebrow">Next race</div>
                    <div className="font-display text-lg tracking-wide mt-1">
                        {label}
                    </div>
                </div>
            )}
            {done ? (
                <div className="font-mono text-sm text-emerald-400">
                    SESSION STARTING…
                </div>
            ) : (
                <div className="flex items-center gap-3 sm:gap-4">
                    <Cell value={d} unit="DAYS" />
                    <span className="text-zinc-700 -mt-3">:</span>
                    <Cell value={h} unit="HOURS" />
                    <span className="text-zinc-700 -mt-3">:</span>
                    <Cell value={m} unit="MIN" />
                    <span className="text-zinc-700 -mt-3">:</span>
                    <Cell value={s} unit="SEC" />
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
export function SectionHeader({ eyebrow, title, subtitle, align = "left" }) {
    const alignClass =
        align === "center" ? "text-center items-center" : "text-left items-start";
    return (
        <div className={`flex flex-col gap-3 ${alignClass}`}>
            {eyebrow && (
                <div className="inline-flex items-center gap-2">
                    <span className="h-px w-8 bg-f1-red" />
                    <span className="label-eyebrow text-f1-red">{eyebrow}</span>
                </div>
            )}
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                {title}
            </h2>
            {subtitle && (
                <p className="max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed">
                    {subtitle}
                </p>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
export function Reveal({ children, delay = 0, y = 24, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// ---------------------------------------------------------------------------
export function ConfidenceBadge({ value = 0.9 }) {
    const pct = Math.round(value * 100);
    const color =
        pct >= 90 ? "text-emerald-400 border-emerald-400/30" :
        pct >= 75 ? "text-amber-400 border-amber-400/30" :
        "text-orange-400 border-orange-400/30";
    return (
        <span
            data-testid="confidence-badge"
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${color} bg-black/30`}
        >
            <span className="font-mono text-[10px] tracking-wider">CONF</span>
            <span className="font-mono text-[10px] font-semibold">{pct}%</span>
        </span>
    );
}

// ---------------------------------------------------------------------------
export function SkeletonRow({ className = "" }) {
    return (
        <div
            className={`h-4 rounded-md shimmer ${className}`}
            data-testid="skeleton"
        />
    );
}

// ---------------------------------------------------------------------------
export function EmptyState({ title, message, icon: Icon }) {
    return (
        <div
            data-testid="empty-state"
            className="flex flex-col items-center justify-center py-16 px-6 text-center"
        >
            {Icon && (
                <div className="h-14 w-14 rounded-full grid place-items-center bg-white/5 border border-white/10 mb-4">
                    <Icon className="h-6 w-6 text-zinc-500" />
                </div>
            )}
            <h3 className="font-display text-2xl tracking-wide">{title}</h3>
            {message && <p className="mt-2 text-sm text-zinc-500 max-w-md">{message}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
export function ErrorState({ message }) {
    return (
        <div
            data-testid="error-state"
            className="flex items-center gap-3 p-4 rounded-lg border border-f1-red/30 bg-f1-red/5"
        >
            <div className="h-2 w-2 rounded-full bg-f1-red" />
            <p className="text-sm text-zinc-300">
                {message || "Something went wrong."}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
export function TireDot({ compound, age }) {
    const colors = {
        soft: "#EF4444",
        medium: "#FBBF24",
        hard: "#FFFFFF",
        inter: "#22C55E",
        wet: "#3B82F6",
    };
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="h-3 w-3 rounded-full ring-1 ring-white/30"
                style={{ background: colors[compound] || "#71717A" }}
            />
            <span className="font-mono text-[10px] text-zinc-400 uppercase">
                {compound}
                {age != null ? ` · ${age}L` : ""}
            </span>
        </span>
    );
}
